import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import { type ConditionOperation } from '~/gql/graphql'
import {
  conditionOperationLabelI18nKeyMap,
  conditionOperations as defaultConditionOperations,
  exists,
} from '~/utils'

export type ConditionOperationSelectorProps = Omit<
  ItemSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  conditionOperations?: ConditionOperation[]
  onChange?: (conditionOperation: ConditionOperation) => void
  value: ConditionOperation
}

export const ConditionOperationSelector = React.forwardRef<
  HTMLSpanElement,
  ConditionOperationSelectorProps
>(({ conditionOperations, onChange, value: valueProp, ...rest }, ref) => {
  const { t } = useTranslation()
  const defaultData = React.useMemo(
    () =>
      defaultConditionOperations.map((conditionOperation) => ({
        label: t(conditionOperationLabelI18nKeyMap[conditionOperation]),
        value: conditionOperation,
      })),
    [t],
  )

  const data = React.useMemo(
    () =>
      exists(conditionOperations)
        ? defaultData.filter(({ value }) => conditionOperations.includes(value))
        : defaultData,
    [conditionOperations, defaultData],
  )
  const value = React.useMemo(
    () => defaultData.find(({ value }) => value === valueProp),
    [defaultData, valueProp],
  )

  const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
    (dataItem: (typeof defaultData)[number]) => {
      onChange?.(dataItem.value)
    },
    [onChange],
  )

  return (
    <ItemSelector
      ref={ref}
      data={data}
      dataItemKey="value"
      textField="label"
      value={value}
      onChange={handleChange}
      {...rest}
    />
  )
})
